import RolesDonutChart, { ChartRoleDatum } from './RolesDonutChart.tsx'
import RolesLegend from './RolesLegend.tsx'
import VisuallyHiddenTable from './VisuallyHiddenTable.tsx'
import { ClubAchievements } from '../../generated'
import { useMemo } from 'react'
import { usePathways } from '../../app/hooks/UsePathways.ts'
import { PathwayCode } from '../../types/Types.ts'
import { formatDateInTimeZone } from '../../utils/TimezoneUtils.ts'

interface ClubAchievementsCardProps {
  achievement: ClubAchievements
}

const ROLE_COLORS = [
  '#56BBC8', // sky blue
  '#6366F1', // indigo
  '#F87171', // coral red
  '#FBBF24', // amber
  '#10B981', // emerald
  '#8B5CF6', // violet
  '#F97316', // orange
  '#64748B', // slate gray
  '#14B8A6', // teal
  '#EC4899', // pink
  '#22D3EE', // cyan
  '#DB4C40', // deep red
]

const ClubAchievementsCard = ({ achievement }: ClubAchievementsCardProps) => {
  const {
    club: { name: clubName, timezone },
    totals,
  } = achievement

  const { labelForCode } = usePathways()

  // Legacy speaker rows have no title — skip them so we only list real speeches.
  const givenSpeeches = useMemo(
    () => (achievement.speeches ?? []).filter((speech) => speech.speechTitle),
    [achievement.speeches]
  )

  const chartData: ChartRoleDatum[] = useMemo(() => {
    const sortedRoleEntries = Object.entries(totals.rolesCount ?? {}).sort(
      ([, countA], [, countB]) => countB - countA
    )

    return sortedRoleEntries.map(([roleName, count], index) => ({
      name: roleName,
      value: count,
      color: ROLE_COLORS[index % ROLE_COLORS.length],
    }))
  }, [totals.rolesCount])

  const totalRoles = totals.rolesScheduled ?? 0
  const hasRoleBreakdown = chartData.length > 0

  const accessibilityRows = useMemo(() => {
    const rows = [
      {
        label: 'Total roles scheduled',
        value: totalRoles,
      },
    ]

    if (hasRoleBreakdown) {
      rows.push(
        ...chartData.map((item) => ({
          label: item.name,
          value: item.value,
        }))
      )
    }

    return rows
  }, [chartData, hasRoleBreakdown, totalRoles])

  return (
    <div className="flex h-full flex-col justify-between rounded-xl border border-gray-200 bg-white p-6 shadow-sm mb-10">
      <header className="mb-6 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h3 className="text-lg font-semibold text-gray-900" title={clubName}>
            Roles for {clubName}
          </h3>
        </div>
      </header>

      <div className="flex flex-1 flex-col gap-6 md:flex-row">
        <RolesDonutChart
          data={chartData}
          total={totalRoles}
          clubName={clubName}
        />
        <div className="flex flex-1 flex-col justify-center">
          {hasRoleBreakdown ? (
            <RolesLegend items={chartData} />
          ) : (
            <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 px-4 py-6 text-center text-sm text-gray-500">
              No roles yet. Complete a meeting role to see it here.
            </div>
          )}
        </div>
      </div>

      {givenSpeeches.length > 0 && (
        <div className="mt-6 border-t border-gray-100 pt-4">
          <h4 className="text-sm font-semibold text-gray-900">
            Speeches given
          </h4>
          <ul className="mt-3 space-y-2">
            {givenSpeeches.map((speech, index) => {
              const meta = [
                labelForCode(speech.pathway as PathwayCode | undefined),
                speech.pathwayLevel
                  ? `Level ${speech.pathwayLevel}`
                  : undefined,
              ]
                .filter(Boolean)
                .join(' · ')
              const givenOn = formatDateInTimeZone(
                speech.meetingTimestampUtc,
                timezone
              )

              return (
                <li
                  key={`${speech.meetingTimestampUtc}-${index}`}
                  className="text-sm text-gray-700">
                  <span className="font-medium text-gray-900">
                    {speech.speechTitle}
                  </span>
                  {(givenOn || meta) && (
                    <span className="block text-xs text-gray-500">
                      {givenOn && (
                        <time
                          dateTime={new Date(
                            speech.meetingTimestampUtc * 1000
                          ).toISOString()}>
                          {givenOn}
                        </time>
                      )}
                      {givenOn && meta && ' · '}
                      {meta}
                    </span>
                  )}
                </li>
              )
            })}
          </ul>
        </div>
      )}

      <VisuallyHiddenTable
        caption={`${clubName} roles summary`}
        rows={accessibilityRows}
      />
    </div>
  )
}

export default ClubAchievementsCard
